import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { auth } from "../../services/firebase";
import { useRole } from "../../contexts/RoleContext";
import { organizeCapturedCareSource } from "./careSourceProcessingClient";
import {
  getProcessingReceipt,
  getReprocessOptions,
} from "./processingReceiptModel";

const CaptureProcessingReceipt = ({
  source = {},
  onComplete,
  savedEntryCount,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { canManageChild } = useRole();
  const inFlight = useRef(false);
  const currentSourceId = useRef(source.id);
  currentSourceId.current = source.id;
  useEffect(() => {
    inFlight.current = false;
    setBusy(false);
  }, [source.id]);
  const [outcome, setOutcome] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    setOutcome(null);
    setError("");
  }, [source.id, source.processingReceipt]);
  useEffect(() => {
    const timer = setInterval(() => tick((value) => value + 1), 10000);
    return () => clearInterval(timer);
  }, []);
  const receipt = getProcessingReceipt(
    outcome ? { ...source, processingReceipt: outcome } : source,
  );
  const displayedCount = savedEntryCount ?? receipt.saved;
  const authorId = source.authorId || source.createdBy || source.capturedBy?.id;
  const canRetry = Boolean(
    auth.currentUser?.uid &&
    (auth.currentUser.uid === authorId || canManageChild?.(source.childId)),
  );
  const retryAvailable =
    canRetry &&
    source.status !== "deleted" &&
    !source.sourceActivityDeletedAt &&
    (receipt.retryTimeline || receipt.retryFacts);

  const retry = async () => {
    if (inFlight.current || !retryAvailable || receipt.running) return;
    const sourceId = source.id;
    inFlight.current = true;
    const branch = receipt.retryTimeline ? "incomplete" : "facts";
    setBusy(true);
    setError("");
    try {
      const result = await organizeCapturedCareSource({
        sourceNoteId: source.id,
        options: getReprocessOptions(source, branch),
      });
      if (currentSourceId.current !== sourceId) return;
      if (result?.processingReceipt) setOutcome(result.processingReceipt);
      if (result?.ok === false)
        throw new Error(
          result.error?.message ||
            result.processingReceipt?.error?.message ||
            "Processing did not finish.",
        );
      if (result?.skipped)
        throw new Error(
          "This capture is already processing. Check its outcome shortly.",
        );
      if (
        !result?.processingReceipt ||
        result.processingReceipt.status !== "succeeded" ||
        (receipt.retryTimeline &&
          !getProcessingReceipt({
            ...source,
            processingReceipt: result.processingReceipt,
          }).saved)
      )
        throw new Error("Processing did not finish. Please try again.");
      window.dispatchEvent(
        new CustomEvent("captureez:timeline-refresh", {
          detail: {
            childId: source.childId,
            sourceNoteId: source.id,
            collection: "dailyLogs",
          },
        }),
      );
      onComplete?.();
    } catch (failure) {
      if (currentSourceId.current === sourceId)
        setError(failure.message || "Unable to reprocess this capture.");
    } finally {
      if (currentSourceId.current === sourceId) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <Stack
      spacing={0.75}
      sx={{ mt: 1 }}
      data-testid="capture-processing-receipt"
    >
      <Typography variant="body2">
        {displayedCount} {displayedCount === 1 ? "entry" : "entries"}{" "}
        {receipt.sourceState && savedEntryCount == null
          ? "originally saved"
          : "saved"}{" "}
        · {receipt.suggested} {receipt.suggested === 1 ? "fact" : "facts"}{" "}
        suggested
      </Typography>
      {receipt.profileSuggested ? (
        <Typography variant="body2">
          {receipt.profileSuggested} additional Profile suggestions
        </Typography>
      ) : null}
      {receipt.sourceState ? (
        <Alert severity="info">
          {receipt.sourceState === "withdrawn"
            ? "Removed from Timeline."
            : "This capture has edited entries."}{" "}
          The original capture is retained for history. Review the current
          entries and linked facts rather than replaying the original
          extraction.
        </Alert>
      ) : null}
      {receipt.running ? (
        <Typography variant="body2">
          Processing… Your original is saved.
        </Typography>
      ) : null}
      {receipt.failed ? (
        <Alert severity="error">
          {receipt.saved || receipt.suggested
            ? "Partly processed. "
            : "Processing failed. "}
          {receipt.error || "Some entries could not be saved."} Your original is
          saved.
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {retryAvailable ? (
        <Button
          disabled={busy || receipt.running}
          onClick={retry}
          sx={{ alignSelf: "flex-start" }}
        >
          {busy ? "Working on it…" : "Try again"}
        </Button>
      ) : null}
    </Stack>
  );
};
export default CaptureProcessingReceipt;
